import React from 'react';
import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { gridPageCountSelector, gridPageSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid';

interface NumberedDataGridPaginationProps {
    maxPagesToShow?: number;
    showPaginationDropdown?: boolean;
}

const NumberedDataGridPagination = ({
    maxPagesToShow = 5,
    showPaginationDropdown = true,
}: NumberedDataGridPaginationProps) => {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    const safeMaxPagesToShow = Math.max(1, maxPagesToShow);
    const safeCurrentPage = Math.min(page, Math.max(pageCount - 1, 0));
    const pageOptions = Array.from({ length: Math.max(pageCount, 1) }, (_, index) => index);

    const getVisiblePages = () => {
        if (pageCount <= safeMaxPagesToShow) {
            return Array.from({ length: pageCount }, (_, index) => index);
        }

        const pages: Array<number | 'ellipsis'> = [];
        const sidePages = Math.floor((safeMaxPagesToShow - 3) / 2);
        const startPage = Math.max(0, page - sidePages);
        const endPage = Math.min(pageCount - 1, page + sidePages);

        const leftBoundary = startPage > 0;
        const rightBoundary = endPage < pageCount - 1;

        if (!leftBoundary) {
            for (let index = 0; index < Math.min(safeMaxPagesToShow - 1, pageCount); index += 1) {
                pages.push(index);
            }
            if (pageCount > safeMaxPagesToShow) {
                pages.push('ellipsis');
                pages.push(pageCount - 1);
            }
            return pages;
        }

        if (!rightBoundary) {
            pages.push(0);
            pages.push('ellipsis');
            for (let index = Math.max(0, pageCount - (safeMaxPagesToShow - 1)); index < pageCount; index += 1) {
                pages.push(index);
            }
            return pages;
        }

        pages.push(0);
        pages.push('ellipsis');

        for (let index = startPage; index <= endPage; index += 1) {
            pages.push(index);
        }

        pages.push('ellipsis');
        pages.push(pageCount - 1);

        return pages;
    };

    const visiblePages = getVisiblePages();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1 }}>
            {showPaginationDropdown && pageCount > 1 && (
                <Select
                    size="small"
                    value={safeCurrentPage}
                    onChange={(event) => apiRef.current.setPage(Number(event.target.value))}
                    sx={{ minWidth: '88px', maxWidth: '88px' }}
                    aria-label="Select page number"
                >
                    {pageOptions.map((pageNumber) => (
                        <MenuItem key={pageNumber} value={pageNumber}>
                            {pageNumber + 1}
                        </MenuItem>
                    ))}
                </Select>
            )}

            <Button
                size="small"
                variant="outlined"
                disabled={page <= 0}
                aria-label="Previous page"
                sx={{ minWidth: '48px', width: '48px', px: 0 }}
                onClick={() => apiRef.current.setPage(Math.max(page - 1, 0))}
            >
                <ChevronLeft />
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                {visiblePages.map((pageNumber, index) => {
                    if (pageNumber === 'ellipsis') {
                        return (
                            <Typography key={`ellipsis-${index}`} color="text.secondary" sx={{ px: 0.5 }}>
                                ...
                            </Typography>
                        );
                    }

                    return (
                        <Button
                            key={pageNumber}
                            size="small"
                            variant={pageNumber === page ? 'contained' : 'text'}
                            color={pageNumber === page ? 'primary' : 'inherit'}
                            sx={{ minWidth: '48px', width: '48px', px: 0 }}
                            onClick={() => apiRef.current.setPage(pageNumber)}
                        >
                            {pageNumber + 1}
                        </Button>
                    );
                })}
            </Box>

            <Button
                size="small"
                variant="outlined"
                disabled={page >= pageCount - 1}
                aria-label="Next page"
                sx={{ minWidth: '48px', width: '48px', px: 0 }}
                onClick={() => apiRef.current.setPage(Math.min(page + 1, pageCount - 1))}
            >
                <ChevronRight />
            </Button>
        </Box>
    );
};

export default NumberedDataGridPagination;
